import { useCallback, useMemo } from 'react';

import { FolderDTOStatus, useActivateFolder, useDeactivateFolder } from '@/api/generated';

enum FolderStatusActions {
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
  RESUME = 'RESUME',
  HOLD = 'HOLD'
}

export const useFolderStatusActions = (options: {
  organizationSlug: string;
  folderSlug: string;
  currentStatus?: FolderDTOStatus;
  onChange: (newStatus: FolderDTOStatus) => void;
}) => {
  const { organizationSlug, folderSlug, currentStatus, onChange } = options;

  const { mutateAsync: activateFolder, isLoading: isActivateFolderLoading } = useActivateFolder();
  const { mutateAsync: deactivateFolder, isLoading: isDeactivateFolderLoading } = useDeactivateFolder();

  const isStatusLoading = isActivateFolderLoading || isDeactivateFolderLoading;

  const statusTransitions = useMemo(
    () => [
      {
        from: FolderDTOStatus.DRAFT,
        to: FolderDTOStatus.ACTIVE,
        name: FolderStatusActions.ACTIVATE,
        handler: () => activateFolder({ organizationSlug, folderSlug })
      },
      {
        from: FolderDTOStatus.INACTIVE,
        to: FolderDTOStatus.ACTIVE,
        name: FolderStatusActions.ACTIVATE,
        handler: () => activateFolder({ organizationSlug, folderSlug })
      },
      {
        from: FolderDTOStatus.ACTIVE,
        to: FolderDTOStatus.INACTIVE,
        name: FolderStatusActions.DEACTIVATE,
        handler: () => deactivateFolder({ organizationSlug, folderSlug })
      }
    ],
    [activateFolder, deactivateFolder, organizationSlug, folderSlug]
  );

  const availableTransitions = useMemo(
    () => statusTransitions.filter(({ from }) => from === currentStatus),
    [currentStatus, statusTransitions]
  );
  const availableStatuses = useMemo(
    () => [currentStatus, ...availableTransitions.map(({ to }) => to)],
    [availableTransitions, currentStatus]
  );
  const isFinalState = useMemo(() => !availableTransitions.length, [availableTransitions]);
  const availableActions = useMemo(() => availableTransitions.map(({ name }) => name), [availableTransitions]);

  const isActivateAvailable = useMemo(
    () => availableActions.includes(FolderStatusActions.ACTIVATE),
    [availableActions]
  );
  const isDeactivateAvailable = useMemo(
    () => availableActions.includes(FolderStatusActions.DEACTIVATE),
    [availableActions]
  );

  const handleStatusChange = useCallback(
    async (newStatus: FolderDTOStatus) => {
      const originalStatus = currentStatus;
      try {
        const transition = availableTransitions.find(({ from, to }) => originalStatus === from && newStatus === to);
        if (!transition) {
          throw new Error(`Transition impossible: ${originalStatus} => ${newStatus}`);
        }
        onChange(newStatus);
        if (transition.handler) {
          await transition.handler();
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Transition is not implemented: ${transition.name}`);
        }
      } catch (e) {
        // TODO: proper logging
        console.error(e); // eslint-disable-line no-console
        onChange(originalStatus!);
      }
    },
    [availableTransitions, currentStatus, onChange]
  );

  const handleActivate = useCallback(() => {
    if (isActivateAvailable) {
      handleStatusChange(FolderDTOStatus.ACTIVE);
    }
  }, [handleStatusChange, isActivateAvailable]);

  const handleDeactivate = useCallback(() => {
    if (isDeactivateAvailable) {
      handleStatusChange(FolderDTOStatus.INACTIVE);
    }
  }, [handleStatusChange, isDeactivateAvailable]);

  return {
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses,
    activate: handleActivate,
    deactivate: handleDeactivate,
    change: handleStatusChange,
    isActivateAvailable,
    isDeactivateAvailable
  };
};
